/**
 * Batch 11A.1 · AI Library — clinician/admin source-management UI for the
 * EXISTING Batch 11A evidence API. Source management only — NOT an AI chat page
 * and NOT wired into any AI feature (grounding is a later batch).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  BookOpen, Upload, Search, FileText, ShieldAlert, CheckCircle2, Archive,
  Eye, AlertTriangle, Layers, ExternalLink, RotateCcw, Info, FlaskConical, ChevronDown,
} from "lucide-react";

const SOURCE_TYPES = ["guideline", "consensus_statement", "systematic_review", "meta_analysis",
  "research_paper", "textbook", "course_material", "functional_lab_protocol", "other"];
const AUDIENCES = ["clinician_only", "patient_safe", "both"];
const STATUSES = ["pending", "approved", "archived"];

const humanize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const STATUS_BADGE = {
  pending: { label: "Pending", cls: "bg-amber-400/15 text-amber-300 border-amber-400/40" },
  approved: { label: "Approved", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  archived: { label: "Archived", cls: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
};
const EXTRACTION_BADGE = {
  extracted: { label: "Ready", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  needs_ocr: { label: "Needs OCR", cls: "bg-amber-400/15 text-amber-300 border-amber-400/40" },
  failed: { label: "Extraction failed", cls: "bg-red-500/15 text-red-300 border-red-500/40" },
  empty: { label: "Empty", cls: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
};
const AUDIENCE_BADGE = {
  clinician_only: { label: "Clinician only", cls: "bg-blue-500/15 text-blue-300 border-blue-500/40" },
  patient_safe: { label: "Patient safe", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  both: { label: "Both", cls: "bg-teal-500/15 text-teal-300 border-teal-500/40" },
};
const SEMANTIC_BADGE = {
  ready: { label: "Semantic ready", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  pending: { label: "Indexing", cls: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
  failed: { label: "Semantic indexing failed", cls: "bg-red-500/15 text-red-300 border-red-500/40" },
};
const PROCESSING_BADGE = {
  uploaded: { label: "Queued…", cls: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
  extracting: { label: "Extracting…", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  chunking: { label: "Creating passages…", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  embedding: { label: "Building semantic index…", cls: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40" },
  ready: { label: "Processed", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Processing failed", cls: "bg-red-500/15 text-red-300 border-red-500/40" },
};
const PROCESSING_ACTIVE = ["uploaded", "extracting", "chunking", "embedding"];

function PillBadge({ map, value, testid }) {
  const m = map[value] || { label: humanize(value), cls: "bg-slate-500/15 text-slate-300 border-slate-500/40" };
  return <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${m.cls}`}>{m.label}</span>;
}

const EMPTY_FORM = {
  title: "", source_type: "guideline", audience: "clinician_only", authors: "",
  year: "", topic: "", body_region: "", doi_or_url: "", evidence_category: "",
};

export default function AILibrary() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || !!user?.is_admin;

  const [sources, setSources] = useState(null);
  const [filters, setFilters] = useState({ status: "all", source_type: "all", audience: "all", q: "" });
  const [selected, setSelected] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [supersedes, setSupersedes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [limits, setLimits] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/evidence/sources");
      setSources(data);
      setSelected((prev) => (prev ? (data.find((x) => x.id === prev.id) || prev) : prev));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the AI Library");
      setSources([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Actual supported upload limits (no magic numbers in the UI).
  useEffect(() => {
    api.get("/evidence/config").then(({ data }) => setLimits(data)).catch(() => {});
  }, []);

  // While any source is still ingesting in the background, poll so the status
  // (Extracting → Creating passages → Building semantic index → Processed) updates live.
  useEffect(() => {
    const list = sources || [];
    if (!list.some((s) => PROCESSING_ACTIVE.includes(s.processing_status))) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [sources, load]);

  const filtered = useMemo(() => {
    const list = sources || [];
    const q = filters.q.trim().toLowerCase();
    return list.filter((s) => {
      if (filters.status !== "all" && s.status !== filters.status) return false;
      if (filters.source_type !== "all" && s.source_type !== filters.source_type) return false;
      if (filters.audience !== "all" && s.audience !== filters.audience) return false;
      if (q) {
        const hay = [s.title, (s.authors || []).join(" "), s.topic, s.body_region, String(s.year || "")]
          .join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sources, filters]);

  const openUpload = (supersedeSource = null) => {
    setSupersedes(supersedeSource?.id || null);
    setForm(supersedeSource
      ? { ...EMPTY_FORM, title: `${supersedeSource.title} (new version)`, source_type: supersedeSource.source_type,
          audience: supersedeSource.audience, topic: supersedeSource.topic || "", body_region: supersedeSource.body_region || "" }
      : EMPTY_FORM);
    setFile(null);
    setUploadOpen(true);
  };

  // Files small enough to cross the ingress proxy in one request go via the
  // original single-shot endpoint. Larger files are sliced and streamed through
  // the chunked endpoints so a 300-500 MB book never hits the body-size limit.
  const SINGLE_SHOT_MAX = 8 * 1024 * 1024; // 8 MB

  const uploadChunked = async () => {
    const init = await api.post("/evidence/sources/chunk/init", {
      filename: file.name, total_size: file.size,
    });
    const { upload_id, chunk_size } = init.data;
    const size = chunk_size || 8 * 1024 * 1024;
    const total = Math.max(1, Math.ceil(file.size / size));
    for (let i = 0; i < total; i++) {
      const slice = file.slice(i * size, Math.min(file.size, (i + 1) * size));
      await api.post(`/evidence/sources/chunk/${upload_id}`, slice, {
        headers: { "Content-Type": "application/octet-stream" },
      });
      setProgress(Math.round(((i + 1) / total) * 100));
    }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
    if (supersedes) fd.append("supersedes", supersedes);
    await api.post(`/evidence/sources/chunk/${upload_id}/complete`, fd);
  };

  const submitUpload = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!file) return toast.error("Please choose a PDF, DOCX or TXT file");
    const maxMb = limits?.max_upload_mb || 500;
    if (file.size > maxMb * 1024 * 1024) {
      return toast.error(`File exceeds the ${maxMb} MB upload limit`);
    }
    setBusy(true);
    setProgress(0);
    try {
      if (file.size > SINGLE_SHOT_MAX) {
        await uploadChunked();
      } else {
        const fd = new FormData();
        fd.append("file", file);
        Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
        if (supersedes) fd.append("supersedes", supersedes);
        await api.post("/evidence/sources", fd);
      }
      toast.success("File stored — processing in the background. It'll be ready for review shortly.");
      setUploadOpen(false);
      await load();
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 413) toast.error(detail || `File exceeds the ${maxMb} MB upload limit`);
      else if (code === 502) toast.error("File storage failed — please retry");
      else if (code === 400) toast.error(detail || "This file could not be accepted");
      else if (code === 404) toast.error("Upload session expired — please try again");
      else toast.error(detail || "Upload failed — please try again");
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const reprocess = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/evidence/sources/${id}/reprocess`);
      toast.success("Reprocessing started…");
      setSelected(data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start reprocessing");
    } finally {
      setBusy(false);
    }
  };

  const act = async (id, action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/evidence/sources/${id}/${action}`);
      toast.success(action === "approve" ? "Source approved" : "Source archived");
      setSelected(data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action}`);
    } finally {
      setBusy(false);
    }
  };

  const viewFile = async (s) => {
    try {
      const res = await api.get(`/evidence/sources/${s.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open the source file");
    }
  };

  return (
    <DashLayout>
      <div className="space-y-6" data-testid="ai-library-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[color:var(--fl-orange)] fl-label">
              <BookOpen size={16} /> AI Library
            </div>
            <h1 className="text-2xl font-semibold mt-1" style={{ color: "var(--fl-text)" }}>Clinical evidence sources</h1>
            <p className="text-sm mt-1" style={{ color: "var(--fl-muted)" }}>
              Manage guidelines, protocols and literature that future AI features will cite. Source management only — not connected to any AI yet.
            </p>
          </div>
          <Button data-testid="ai-library-upload-btn" onClick={() => openUpload(null)} className="shrink-0">
            <Upload size={16} className="mr-2" /> Upload source
          </Button>
        </div>

        {/* No-patient-data reminder */}
        <div data-testid="ai-library-patient-notice" className="flex items-start gap-2 rounded-lg px-4 py-3 text-[13px]"
          style={{ background: "color-mix(in oklab, var(--fl-orange) 8%, transparent)", border: "1px solid color-mix(in oklab, var(--fl-orange) 35%, transparent)", color: "var(--fl-text)" }}>
          <ShieldAlert size={16} className="mt-0.5 text-[color:var(--fl-orange)] shrink-0" />
          <span>
            <b>AI Library is for clinical evidence and Functional LAB protocols — do not upload patient records here.</b> Clinical notes,
            MRI/report files, investigation documents and identifiable records belong in the patient's existing areas, never in the shared evidence library.
          </span>
        </div>

        {/* Evidence retrieval preview (clinician/admin QA — no AI generation) */}
        <RetrievalPreview onViewFile={viewFile} />

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ai-library-filters">
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--fl-muted-2)" }} />
            <Input data-testid="ai-library-search" placeholder="Search title, author, topic…" className="pl-9"
              value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{humanize(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.source_type} onValueChange={(v) => setFilters((f) => ({ ...f, source_type: v }))}>
            <SelectTrigger data-testid="filter-source-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {SOURCE_TYPES.map((s) => <SelectItem key={s} value={s}>{humanize(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.audience} onValueChange={(v) => setFilters((f) => ({ ...f, audience: v }))}>
            <SelectTrigger data-testid="filter-audience"><SelectValue placeholder="Audience" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All audiences</SelectItem>
              {AUDIENCES.map((s) => <SelectItem key={s} value={s}>{humanize(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        {sources === null ? (
          <div className="text-sm" style={{ color: "var(--fl-muted)" }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="ai-library-empty" className="rounded-xl p-10 text-center text-sm"
            style={{ border: "1px dashed var(--fl-border)", color: "var(--fl-muted)" }}>
            No sources match your filters. Upload the first evidence source to get started.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ai-library-list">
            {filtered.map((s) => (
              <button key={s.id} data-testid={`source-card-${s.id}`} onClick={() => setSelected(s)}
                className="text-left rounded-xl p-4 transition-all hover:translate-y-[-2px]"
                style={{ background: "var(--fl-card, rgba(255,255,255,0.03))", border: "1px solid var(--fl-border)" }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={16} className="shrink-0 text-[color:var(--fl-orange)]" />
                    <span className="font-medium truncate" style={{ color: "var(--fl-text)" }}>{s.title}</span>
                  </div>
                  <PillBadge map={STATUS_BADGE} value={s.status} testid={`status-${s.id}`} />
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>
                  {(s.authors || []).slice(0, 3).join(", ") || "—"}{s.year ? ` · ${s.year}` : ""}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "color-mix(in oklab, var(--fl-text) 7%, transparent)", color: "var(--fl-muted)" }}>{humanize(s.source_type)}</span>
                  {s.body_region && <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "color-mix(in oklab, var(--fl-text) 7%, transparent)", color: "var(--fl-muted)" }}>{s.body_region}</span>}
                  <PillBadge map={AUDIENCE_BADGE} value={s.audience} testid={`audience-${s.id}`} />
                  {PROCESSING_ACTIVE.includes(s.processing_status) ? (
                    <PillBadge map={PROCESSING_BADGE} value={s.processing_status} testid={`processing-${s.id}`} />
                  ) : (
                    <>
                      {s.processing_status === "failed" && <PillBadge map={PROCESSING_BADGE} value="failed" testid={`processing-${s.id}`} />}
                      <PillBadge map={EXTRACTION_BADGE} value={s.extraction_status} testid={`extraction-${s.id}`} />
                      {s.semantic_status && <PillBadge map={SEMANTIC_BADGE} value={s.semantic_status} testid={`semantic-${s.id}`} />}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[11px] mt-3" style={{ color: "var(--fl-muted-2)" }}>
                  <span className="flex items-center gap-1"><Layers size={12} /> {s.chunk_count ?? 0} passages</span>
                  <span>v{s.version || 1}</span>
                  {s.superseded_by && <span className="text-amber-300">superseded</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <SourceDetail
        source={selected} onClose={() => setSelected(null)} isAdmin={isAdmin}
        busy={busy} onAct={act} onViewFile={viewFile} onReprocess={reprocess}
        onNewVersion={(s) => { setSelected(null); openUpload(s); }}
      />

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-library-upload-dialog">
          <DialogHeader><DialogTitle>{supersedes ? "Upload new version" : "Upload evidence source"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-start gap-2 text-[12px] rounded-md px-3 py-2" style={{ background: "color-mix(in oklab, var(--fl-text) 5%, transparent)", color: "var(--fl-muted)" }}>
              <Info size={14} className="mt-0.5 shrink-0" />
              Only upload material that Functional LAB is permitted to store and use internally. Do not upload patient records.
            </div>
            <div>
              <label className="fl-label text-[11px]">File · PDF, DOCX or TXT · max {limits?.max_upload_mb || 500} MB *</label>
              <input data-testid="upload-file" type="file" accept=".pdf,.docx,.txt"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--fl-orange)] file:text-black file:text-sm" />
              {busy && progress > 0 && (
                <div data-testid="upload-progress" className="mt-2">
                  <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: "color-mix(in oklab, var(--fl-text) 10%, transparent)" }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: "var(--fl-orange)" }} />
                  </div>
                  <div className="text-[11px] mt-1" style={{ color: "var(--fl-muted)" }}>Uploading… {progress}%</div>
                </div>
              )}
            </div>
            <div>
              <label className="fl-label text-[11px]">Title *</label>
              <Input data-testid="upload-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. BJSM Patellar Tendinopathy Guideline" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="fl-label text-[11px]">Source type *</label>
                <Select value={form.source_type} onValueChange={(v) => setForm((f) => ({ ...f, source_type: v }))}>
                  <SelectTrigger data-testid="upload-source-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCE_TYPES.map((s) => <SelectItem key={s} value={s}>{humanize(s)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="fl-label text-[11px]">Audience *</label>
                <Select value={form.audience} onValueChange={(v) => setForm((f) => ({ ...f, audience: v }))}>
                  <SelectTrigger data-testid="upload-audience"><SelectValue /></SelectTrigger>
                  <SelectContent>{AUDIENCES.map((s) => <SelectItem key={s} value={s}>{humanize(s)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
              <b>Patient safe</b> means the source may later be retrieved for patient-facing AI explanations. Uploading a source does <b>not</b> make it available to the Patient AI Coach — AI grounding is not connected yet.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="fl-label text-[11px]">Authors</label><Input data-testid="upload-authors" value={form.authors} onChange={(e) => setForm((f) => ({ ...f, authors: e.target.value }))} placeholder="Smith J; Doe A" /></div>
              <div><label className="fl-label text-[11px]">Year</label><Input data-testid="upload-year" value={form.year} onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))} placeholder="2021" /></div>
              <div><label className="fl-label text-[11px]">Topic</label><Input data-testid="upload-topic" value={form.topic} onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))} placeholder="tendinopathy" /></div>
              <div><label className="fl-label text-[11px]">Body region</label><Input data-testid="upload-body-region" value={form.body_region} onChange={(e) => setForm((f) => ({ ...f, body_region: e.target.value }))} placeholder="knee" /></div>
              <div><label className="fl-label text-[11px]">DOI / URL</label><Input data-testid="upload-doi" value={form.doi_or_url} onChange={(e) => setForm((f) => ({ ...f, doi_or_url: e.target.value }))} placeholder="https://doi.org/…" /></div>
              <div><label className="fl-label text-[11px]">Evidence category</label><Input data-testid="upload-category" value={form.evidence_category} onChange={(e) => setForm((f) => ({ ...f, evidence_category: e.target.value }))} placeholder="high / moderate / …" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} data-testid="upload-cancel">Cancel</Button>
            <Button onClick={submitUpload} disabled={busy} data-testid="upload-submit">{busy ? (progress > 0 ? `Uploading… ${progress}%` : "Uploading…") : "Upload"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}

function SourceDetail({ source, onClose, isAdmin, busy, onAct, onViewFile, onReprocess, onNewVersion }) {
  const [preview, setPreview] = useState(null);
  const [offset, setOffset] = useState(0);
  const PAGE = 5;

  const loadPreview = useCallback(async (src, off) => {
    if (!src) return;
    try {
      const { data } = await api.get(`/evidence/sources/${src.id}/chunks`, { params: { limit: PAGE, offset: off } });
      setPreview(data);
    } catch {
      setPreview({ total: 0, chunks: [] });
    }
  }, []);

  useEffect(() => {
    setOffset(0);
    if (source) loadPreview(source, 0); else setPreview(null);
  }, [source, loadPreview]);

  if (!source) return null;
  const s = source;
  const unsafeExtraction = s.extraction_status === "failed" || s.extraction_status === "needs_ocr";
  const isProcessing = PROCESSING_ACTIVE.includes(s.processing_status);
  const canApprove = !isProcessing && s.processing_status !== "failed";

  return (
    <Dialog open={!!source} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="source-detail-dialog">
        <DialogHeader><DialogTitle className="pr-6">{s.title}</DialogTitle></DialogHeader>

        <div className="flex flex-wrap gap-2">
          <PillBadge map={STATUS_BADGE} value={s.status} testid="detail-status" />
          <PillBadge map={AUDIENCE_BADGE} value={s.audience} testid="detail-audience" />
          <PillBadge map={EXTRACTION_BADGE} value={s.extraction_status} testid="detail-extraction" />
          {s.semantic_status && <PillBadge map={SEMANTIC_BADGE} value={s.semantic_status} testid="detail-semantic" />}
          <span className="text-[11px] px-2 py-0.5 rounded-full border" style={{ borderColor: "var(--fl-border)", color: "var(--fl-muted)" }}>v{s.version || 1}</span>
        </div>

        {/* Background ingestion status */}
        {isProcessing && (
          <div data-testid="detail-processing" className="flex items-center gap-2 text-[12px] rounded-md px-3 py-2 mt-1"
            style={{ background: "rgba(56,189,248,.1)", border: "1px solid rgba(56,189,248,.35)", color: "#7dd3fc" }}>
            <RotateCcw size={14} className="animate-spin shrink-0" />
            Processing document… {PROCESSING_BADGE[s.processing_status]?.label}
          </div>
        )}
        {s.processing_status === "failed" && (
          <div data-testid="detail-processing-failed" className="flex items-start gap-2 text-[12px] rounded-md px-3 py-2 mt-1"
            style={{ background: "rgba(239,68,68,.12)", border: "1px solid rgba(239,68,68,.4)", color: "#fca5a5" }}>
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            {s.processing_error ? `${s.processing_error} ` : "Processing failed. "}The stored file is kept — retry without re-uploading.
          </div>
        )}

        {/* Metadata grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mt-2">
          <Meta label="Authors" value={(s.authors || []).join(", ") || "—"} />
          <Meta label="Year" value={s.year || "—"} />
          <Meta label="Source type" value={humanize(s.source_type)} />
          <Meta label="Evidence category" value={s.evidence_category || "—"} />
          <Meta label="Topic" value={s.topic || "—"} />
          <Meta label="Body region" value={s.body_region || "—"} />
          <Meta label="Pages" value={s.page_count || "—"} />
          <Meta label="Passages indexed" value={s.chunk_count ?? 0} />
          <Meta label="DOI / URL" value={s.doi_or_url || "—"} />
          <Meta label="File" value={`${(s.ext || "").toUpperCase()} · ${Math.round((s.size_bytes || 0) / 1024)} KB`} />
          <Meta label="Uploaded by" value={s.uploaded_by_name || "—"} />
          <Meta label="Approved by" value={s.approved_by_name || "—"} />
        </div>

        {/* Version / supersession */}
        {(s.supersedes || s.superseded_by) && (
          <div className="text-[12px] rounded-md px-3 py-2 mt-1" style={{ background: "color-mix(in oklab, var(--fl-text) 5%, transparent)", color: "var(--fl-muted)" }} data-testid="detail-version-info">
            {s.supersedes && <div>This version supersedes an earlier source ({s.supersedes.slice(0, 8)}…).</div>}
            {s.superseded_by && <div>Superseded by a newer version ({s.superseded_by.slice(0, 8)}…) — kept for provenance.</div>}
          </div>
        )}

        {/* Approval safety warning */}
        {s.status === "pending" && unsafeExtraction && (
          <div className="flex items-start gap-2 text-[12px] rounded-md px-3 py-2 mt-1" data-testid="detail-extraction-warning"
            style={{ background: "rgba(245,158,11,.12)", border: "1px solid rgba(245,158,11,.4)", color: "#fcd34d" }}>
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            {s.extraction_status === "needs_ocr"
              ? "This looks like a scanned/image PDF. Text extraction found little content — it may hold no usable passages for AI retrieval even if approved."
              : "Text extraction failed for this source — it has no indexed passages and will not provide usable AI evidence."}
          </div>
        )}

        {/* Extraction preview */}
        <div className="mt-2">
          <div className="flex items-center justify-between">
            <div className="fl-label text-[11px]">Extraction preview (QA)</div>
            <Button size="sm" variant="outline" onClick={() => onViewFile(s)} data-testid="detail-view-source"><Eye size={14} className="mr-1.5" /> View source</Button>
          </div>
          <div className="mt-2 space-y-2" data-testid="detail-chunk-preview">
            {preview === null ? (
              <div className="text-xs" style={{ color: "var(--fl-muted)" }}>Loading preview…</div>
            ) : (preview.chunks || []).length === 0 ? (
              <div className="text-xs" style={{ color: "var(--fl-muted)" }}>No extracted passages to preview.</div>
            ) : (
              preview.chunks.map((c) => (
                <div key={c.id} className="rounded-md p-3 text-[13px]" style={{ background: "color-mix(in oklab, var(--fl-text) 4%, transparent)", border: "1px solid var(--fl-border)" }}>
                  <div className="flex gap-2 text-[11px] mb-1" style={{ color: "var(--fl-muted-2)" }}>
                    {c.page != null && <span>Page {c.page}</span>}
                    {c.section && <span>· {c.section}</span>}
                    <span>· #{c.seq + 1}</span>
                  </div>
                  <div style={{ color: "var(--fl-text)" }} className="line-clamp-4">{c.text}</div>
                </div>
              ))
            )}
            {preview && preview.total > PAGE && (
              <div className="flex items-center justify-between text-xs pt-1" style={{ color: "var(--fl-muted)" }}>
                <span>Showing {offset + 1}–{Math.min(offset + PAGE, preview.total)} of {preview.total}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={offset === 0} data-testid="preview-prev"
                    onClick={() => { const o = Math.max(0, offset - PAGE); setOffset(o); loadPreview(s, o); }}>Prev</Button>
                  <Button size="sm" variant="outline" disabled={offset + PAGE >= preview.total} data-testid="preview-next"
                    onClick={() => { const o = offset + PAGE; setOffset(o); loadPreview(s, o); }}>Next</Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => onNewVersion(s)} data-testid="detail-new-version"><RotateCcw size={14} className="mr-1.5" /> Upload new version</Button>
          {s.doi_or_url && (
            <Button variant="outline" onClick={() => window.open(s.doi_or_url, "_blank", "noopener")} data-testid="detail-open-doi"><ExternalLink size={14} className="mr-1.5" /> Open DOI/URL</Button>
          )}
          {s.processing_status === "failed" && (
            <Button variant="outline" onClick={() => onReprocess(s.id)} disabled={busy} data-testid="detail-reprocess"><RotateCcw size={14} className="mr-1.5" /> Retry processing</Button>
          )}
          {isAdmin && s.status === "pending" && (
            <Button onClick={() => onAct(s.id, "approve")} disabled={busy || !canApprove} data-testid="detail-approve"><CheckCircle2 size={14} className="mr-1.5" /> Approve</Button>
          )}
          {isAdmin && s.status === "pending" && !canApprove && (
            <span className="text-[11px] self-center" style={{ color: "var(--fl-muted-2)" }} data-testid="detail-approve-blocked">
              {isProcessing ? "Approval available once processing is complete." : "Retry processing before approving."}
            </span>
          )}
          {isAdmin && s.status !== "archived" && (
            <Button variant="outline" onClick={() => onAct(s.id, "archive")} disabled={busy} data-testid="detail-archive"><Archive size={14} className="mr-1.5" /> Archive</Button>
          )}
          {!isAdmin && s.status === "pending" && (
            <span className="text-[11px] self-center" style={{ color: "var(--fl-muted-2)" }} data-testid="detail-approval-note">Approval is handled by an admin / clinical admin.</span>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="fl-label text-[10px]" style={{ color: "var(--fl-muted-2)" }}>{label}</div>
      <div style={{ color: "var(--fl-text)" }} className="break-words">{value}</div>
    </div>
  );
}

function RetrievalPreview({ onViewFile }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [topic, setTopic] = useState("");
  const [bodyRegion, setBodyRegion] = useState("");
  const [audience, setAudience] = useState("clinician");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return toast.error("Enter a clinical question to test");
    setBusy(true);
    try {
      const { data } = await api.post("/evidence/retrieve", {
        query: q.trim(), audience,
        topic: topic.trim() || null, body_region: bodyRegion.trim() || null, limit: 8,
      });
      setRes(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retrieval failed");
      setRes(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--fl-border)" }} data-testid="retrieval-preview">
      <button data-testid="preview-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3"
        style={{ background: "color-mix(in oklab, var(--fl-text) 4%, transparent)" }}>
        <span className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--fl-text)" }}>
          <FlaskConical size={16} className="text-[color:var(--fl-orange)]" /> Test evidence retrieval
          <span className="text-[11px] font-normal" style={{ color: "var(--fl-muted-2)" }}>· QA only — no AI generation</span>
        </span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} style={{ color: "var(--fl-muted)" }} />
      </button>

      {open && (
        <div className="p-4 space-y-3">
          <p className="text-[12px]" style={{ color: "var(--fl-muted)" }}>
            Enter a clinical question to see which approved passages the hybrid retrieval engine would hand to a future AI feature. Nothing is generated or sent to an LLM.
          </p>
          <Input data-testid="preview-query" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="e.g. What evidence is relevant to fear of loading after ACL reconstruction?" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input data-testid="preview-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Topic (optional)" />
            <Input data-testid="preview-body-region" value={bodyRegion} onChange={(e) => setBodyRegion(e.target.value)} placeholder="Body region (optional)" />
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="preview-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="clinician">Clinician</SelectItem>
                <SelectItem value="patient">Patient-safe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={search} disabled={busy} data-testid="preview-search-btn">
            <Search size={15} className="mr-2" /> {busy ? "Searching…" : "Search evidence"}
          </Button>

          {res && res.status === "no_relevant_evidence" && (
            <div data-testid="preview-no-results" className="flex items-start gap-2 rounded-md px-3 py-3 text-[13px] mt-1"
              style={{ background: "color-mix(in oklab, var(--fl-text) 5%, transparent)", color: "var(--fl-muted)" }}>
              <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-300" />
              No sufficiently relevant approved evidence was found. Pending, archived or weak sources are never substituted.
            </div>
          )}

          {res && res.status === "ok" && (
            <div className="space-y-2 mt-1" data-testid="preview-results">
              <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
                {res.results.length} passage{res.results.length === 1 ? "" : "s"} · method: {res.retrieval_method}
              </div>
              {res.results.map((r) => (
                <div key={r.chunk_id} data-testid={`preview-result-${r.source_id}`} className="rounded-lg p-3"
                  style={{ background: "var(--fl-card, rgba(255,255,255,0.03))", border: "1px solid var(--fl-border)" }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate" style={{ color: "var(--fl-text)" }}>{r.title}</div>
                      <div className="text-[11px]" style={{ color: "var(--fl-muted)" }}>
                        {(r.authors || []).slice(0, 3).join(", ") || "—"}{r.year ? ` · ${r.year}` : ""} · {humanize(r.source_type)}
                        {r.page != null ? ` · p.${r.page}` : ""}{r.section ? ` · ${r.section}` : ""}
                      </div>
                    </div>
                    <PillBadge map={AUDIENCE_BADGE} value={r.audience} testid={`preview-audience-${r.source_id}`} />
                  </div>
                  <div className="text-[13px] mt-2 line-clamp-4" style={{ color: "var(--fl-text)" }}>{r.text}</div>
                  <div className="flex items-center gap-3 mt-2">
                    <Button size="sm" variant="outline" data-testid={`preview-open-source-${r.source_id}`}
                      onClick={() => onViewFile({ id: r.source_id })}><Eye size={13} className="mr-1.5" /> Open source</Button>
                    <details className="text-[11px]" data-testid={`preview-details-${r.chunk_id}`}>
                      <summary className="cursor-pointer select-none" style={{ color: "var(--fl-muted)" }}>Retrieval details</summary>
                      <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5" style={{ color: "var(--fl-muted)" }}>
                        <span>Semantic similarity: <b style={{ color: "var(--fl-text)" }}>{r.semantic_similarity}</b></span>
                        <span>Lexical score: <b style={{ color: "var(--fl-text)" }}>{r.lexical_score}</b></span>
                        <span>Hybrid score: <b style={{ color: "var(--fl-text)" }}>{r.hybrid_score}</b></span>
                        <span>Exact-term match: <b style={{ color: "var(--fl-text)" }}>{String(r.exact_term_match)}</b></span>
                        <span>Retrieval method: <b style={{ color: "var(--fl-text)" }}>{r.retrieval_method}</b></span>
                      </div>
                    </details>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
